import React, { FormEvent, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Formik,
  Field,
  FormikProps,
  FieldArray,
  FieldArrayRenderProps,
  FormikErrors,
} from "formik";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import { Theme } from "@mui/material/styles";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import { Loading } from "../../../components/Loading";
import { useFirebaseAuth } from "../../../hooks/useFirebaseAuth";
import { vehicleExpenseApi } from "../../vehicleExpense/apiService";
import ExpenseForm from "../../vehicleExpense/components/ExpenseForm";
import {
  displayErrorToast,
  ExpenseEntry,
  ExpenseFormData,
  getEmptyExpenseEntry,
  hasOtherExpense,
} from "../../vehicleExpense/components/types";
import Grid from "@mui/material/Grid";
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Edit,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Description,
  Visibility,
} from "@mui/icons-material";
import { handleExpenseSubmit } from "../utils/handleExpenseSubmit";
import { useToast } from "../../../components/Toast";
import Divider from "@mui/material/Divider";
import DeletedExpenseDetails from "../components/DeletedExpenseDetails";
import { bookingApi } from "../apiService";
import { formatCurrency } from "../../../utils/utils";
import {
  TableCell,
  Table,
  TableRow,
  TableHead,
  TableContainer,
  TableBody,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { tableCellStyles, tableHeaderStyles } from "../../../utils/styles";
import { DocumentUploadSection } from "../../../components/DocumentUploadSection";
import { UploadedFile } from "../../../components/FileUploadSection";
import { fetchDocuments } from "../../../utils/FetchDocuments";
import { v4 as uuidv4 } from "uuid";
import { usePlanStatus } from "../../../hooks/usePlanStatus";
import { validationSchema } from "../../vehicleExpense/validationSchema";
import { vehicleApi, Vehicle } from "../../../services/api";
import GenericOtherExpenseFields from "../../vehicleExpense/components/GenericOtherExpenseFields";
import { AddVehicleAndOwner } from "./DailyLogs";
import AddScheduleVehicleModal from "../components/AddScheduleVehicleModal";

const EXPENSE_TYPES = [
  "Fuel",
  "Maintenance / Repair",
  "Driver/Cleaner",
  "Parking",
  "Challan",
  "Other",
] as const;

const OUTSOURCED_EXPENSE_TYPES = [
  ...EXPENSE_TYPES,
  "Outsourced Owner",
] as const;

// Add transformation function for backend expense data
const transformBackendExpenseToFormData = (
  backendExpense: any
): ExpenseEntry => {
  // Map backend expense types to frontend types
  const expenseTypeMap: Record<string, string> = {
    FUEL: "Fuel",
    REPAIR: "Maintenance / Repair",
    DRIVER_CLEANER: "Driver/Cleaner",
    PARKING: "Parking",
    OTHER: "Other",
    INSURANCE: "Insurance",
    CHALLAN: "Challan",
    OUTSOURCED_OWNER: "Outsourced Owner",
  };

  const mappedType = expenseTypeMap[backendExpense.expenseType] || "Other";

  const transformedExpense: ExpenseEntry = {
    id: backendExpense.id,
    type: mappedType,
    expenseDate: backendExpense.expenseDate,
    amount: backendExpense.amount,
    paidTo: backendExpense.paidTo || "",
    location: backendExpense.location || "",
    contactNumber: backendExpense.contactNumber || "",
    notes: backendExpense.notes || "",
    isEdited: false,
    isDeleted: false,

    // Initialize with empty defaults
    payments: backendExpense.payments || [],
    deletedPayments: backendExpense.deletedPayments || [],
    remainingAmount: 0,
    fuelType: "",
    liters: "",
    pricePerLitre: "",
    bulkDiscountApplied: "",
    petrolPumpId: "",
    odometerReading: "",
    serviceType: "",
    serviceProvider: "",
    serviceProviderId: "",
    maintenanceItem: "",
    customMaintenanceItem: "",
    warrantyPeriod: "",
    warrantyStartDate: "",
    warrantyEndDate: "",
    customWarrantyPeriod: "",
    nextServiceDue: "",
    invoiceNumber: "",
    workOrderNumber: "",
    category: undefined,
    referenceId: "",
    customCategory: "",
    challanAction: "",
    updatedAt: backendExpense.updatedAt || "",
    deletedDetails: backendExpense.deletedDetails || [],
    vehicleId: backendExpense.vehicleId || "",
    masterReferenceId: backendExpense.masterReferenceId || "",
    masterCategory: backendExpense.masterCategory || "",
    companyId: backendExpense.companyId || "",
    createdBy: backendExpense.createdBy || "",
  };

  // Fill in specific details based on expense type and details
  if (backendExpense.details) {
    const details = backendExpense.details;

    if (backendExpense.expenseType === "FUEL" && details) {
      transformedExpense.fuelType = details.fuelType || "";
      transformedExpense.liters = details.liters || "";
      transformedExpense.pricePerLitre = details.pricePerLitre || "";
      transformedExpense.bulkDiscountApplied =
        details.bulkDiscountApplied || "";
      transformedExpense.petrolPumpId = details.petrolPumpId || "";
      transformedExpense.odometerReading =
        details.odometerReading?.toString() || "";
      transformedExpense.fuelCardId = details.fuelCardId || "";
    }

    if (
      (backendExpense.expenseType === "DRIVER_CLEANER" ||
        backendExpense.expenseType === "PARKING" ||
        backendExpense.expenseType === "OTHER" ||
        backendExpense.expenseType === "OUTSOURCED_OWNER") &&
      details
    ) {
      transformedExpense.category = details.category;
      transformedExpense.referenceId = details.referenceId || "";
      transformedExpense.customCategory = details.customCategory || "";
    }

    if (backendExpense.expenseType === "CHALLAN" && details) {
      transformedExpense.challanAction = details.challanAction || "";
      transformedExpense.responsibleParty = details.responsibleParty || "";
      transformedExpense.responsiblePartyContact =
        details.responsiblePartyContact || "";
      transformedExpense.responsiblePartyName =
        details.responsiblePartyName || "";
      transformedExpense.challanNumber = details.challanNumber || "";
      transformedExpense.challanType = details.challanType || "";
    }

    if (
      ["REPAIR", "MAINTENANCE"].includes(backendExpense.expenseType) &&
      details
    ) {
      transformedExpense.maintenanceItem = details.maintenanceItem || "";
      transformedExpense.customMaintenanceItem =
        details.customMaintenanceItem || "";
      transformedExpense.warrantyPeriod = details.warrantyPeriod || "";
      transformedExpense.warrantyStartDate = details.warrantyStartDate || "";
      transformedExpense.warrantyEndDate = details.warrantyEndDate || "";
      transformedExpense.customWarrantyPeriod =
        details.customWarrantyPeriod || "";
      transformedExpense.nextServiceDue = details.nextServiceDue || "";
      transformedExpense.invoiceNumber = details.invoiceNumber || "";
      transformedExpense.workOrderNumber = details.workOrderNumber || "";
      transformedExpense.odometerReading = details.odometerReading || "";
      transformedExpense.actionType = details.actionType || "";
      transformedExpense.serviceProviderId = details.serviceProviderId || "";
      transformedExpense.serviceType = details.serviceType || "";
    }
  }

  if (
    backendExpense?.uploadedFiles &&
    backendExpense?.uploadedFiles?.length > 0
  ) {
    transformedExpense.uploadedFiles = backendExpense?.uploadedFiles;
  }

  return transformedExpense;
};

function RentalExpenses() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useFirebaseAuth();
  const [isLoading, setIsLoading] = useState(false);
  const { rentalJourneyId } = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasLoadedData, setHasLoadedData] = useState(false);
  const [changedExpenses, setChangedExpenses] = useState<ExpenseEntry[]>([]);
  const [showChangeLogs, setShowChangeLogs] = useState(false);
  const { showToast } = useToast();
  const [searchParams] = useSearchParams();
  const vehicleId = searchParams.get("vehicleId");
  const vehicleNumber = searchParams.get("vehicleNumber");
  const [isEditMode, setIsEditMode] = useState(false);
  const [isAddMode, setIsAddMode] = useState(false);
  const [initialValues, setInitialValues] = useState<ExpenseFormData>({
    vehicleNumber: vehicleNumber || "",
    vehicleId: vehicleId || "",
    acquisitionType: "",
    vehicle: null,
    expenses: [],
    odometerReadings: new Map<
      string,
      {
        index: number;
        odometerReading: number;
        fuelQuantity: number;
        readingDate: string;
      }
    >(),
  });
  const [formKey, setFormKey] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [vehicleModalOpen, setVehicleModalOpen] = useState<boolean>(false);
  const [showAllExpenseTypes, setShowAllExpenseTypes] = useState(false);
  const { planStatus } = usePlanStatus();
  const isScheduled = searchParams.get("type") === "SCHEDULED";

  const fetchExpenseDetails = async () => {
    try {
      setIsLoading(true);
      const response = await Promise.all([
        bookingApi.getExpenseDetails(rentalJourneyId!),
        bookingApi.getDeletedExpenseDetails(rentalJourneyId!),
      ]);
      const [expenseDetails, deletedExpenseDetails] = response;
      if (expenseDetails && Array.isArray(expenseDetails)) {
        // Transform backend expense data to component format
        const transformedExpenses = expenseDetails.map(
          transformBackendExpenseToFormData
        );

        const vehicle = await vehicleApi.search(
          vehicleId || vehicleNumber || ""
        );

        setInitialValues({
          vehicleNumber: vehicleNumber || "",
          vehicleId: vehicleId || "",
          vehicle: vehicle[0] || null,
          acquisitionType: vehicle[0]?.acquisitionType || "",
          expenses: transformedExpenses,
        });
        setHasLoadedData(true);
      }
      if (deletedExpenseDetails && Array.isArray(deletedExpenseDetails)) {
        setChangedExpenses(deletedExpenseDetails);
      }
    } catch (error) {
      console.error("Error fetching expense details:", error);
      // Keep default values on error
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (rentalJourneyId && !isScheduled) {
      fetchExpenseDetails();
    }
  }, []);

  const fetchOdometerReadings = async () => {
    vehicleExpenseApi
      .getCurrentOdometerReadings(vehicleId || "", "RENTAL_EXPENSE")
      .then((currentOdometerReadings) => {
        const odometerReadingsMap = new Map<
          string,
          {
            index: number;
            odometerReading: number;
            fuelQuantity: number;
            readingDate: string;
          }
        >();

        currentOdometerReadings.forEach((reading: any, index: number) => {
          const key = reading.expenseId || uuidv4();

          // Convert liters to number, default to 0 if null or invalid
          const fuelQuantity = reading.liters ? parseFloat(reading.liters) : 0;

          // Create the reading object with negative index to avoid conflicts with form entries
          const readingObj = {
            index: -(index + 1), // Use negative index for existing readings
            odometerReading: reading.odometerReading,
            fuelQuantity: fuelQuantity,
            readingDate: reading.expenseDate,
          };

          odometerReadingsMap.set(key, readingObj);
        });
        // Set the map in formik values
        setInitialValues({
          ...initialValues,
          odometerReadings: odometerReadingsMap,
        });
      });
  };

  const handleBack = () => {
    navigate("/rental");
  };

  const renderExpenseTypeSelector = (
    arrayHelpers: FieldArrayRenderProps,
    expenses: ExpenseEntry[],
    vehicleSelected: boolean,
    acquisitionType: string
  ) => {
    if (!vehicleSelected) {
      return (
        <Paper
          elevation={3}
          sx={{
            p: 3,
            borderRadius: 2,
            position: "sticky",
            top: 24,
            bgcolor: "action.disabledBackground",
          }}
        >
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            No vehicle number provided
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please provide a valid vehicle number to start adding expenses.
          </Typography>
        </Paper>
      );
    }

    // Get unique expense types that are already added
    const existingTypes = new Set(expenses.map((expense) => expense.type));

    // Filter out expense types that are already added
    const availableTypes =
      acquisitionType === "Outsourced"
        ? OUTSOURCED_EXPENSE_TYPES.filter((type) => !existingTypes.has(type))
        : EXPENSE_TYPES.filter((type) => !existingTypes.has(type));

    if (availableTypes.length === 0) {
      return (
        <Paper
          elevation={3}
          sx={{
            p: 3,
            borderRadius: 2,
            position: "sticky",
            top: 24,
          }}
        >
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            All expense types added
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Use the "Add Another" button in each section to add more expenses of
            the same type.
          </Typography>
        </Paper>
      );
    }

    // For outsourced, show priority types first with view more button
    if (acquisitionType === "Outsourced") {
      const priorityTypes = ["Outsourced Owner", "Driver/Cleaner", "Other"];
      const remainingTypes = availableTypes.filter(
        (type) => !priorityTypes.includes(type)
      );

      const showPriorityTypes = priorityTypes.filter(
        (type) => !existingTypes.has(type)
      );
      const showRemainingTypes = remainingTypes.filter(
        (type) => !existingTypes.has(type)
      );

      // If all priority types are added, show remaining types by default
      const shouldShowAllTypes = showPriorityTypes.length === 0;

      return (
        <Paper
          elevation={3}
          sx={{
            p: 1.5,
            borderRadius: 2,
            position: "sticky",
            top: 16,
          }}
        >
          <Typography variant="subtitle1" sx={{ mb: 1, fontSize: "0.75rem" }}>
            Select expense type to add:
          </Typography>
          <Stack
            direction="row"
            flexWrap="wrap"
            sx={{ gap: 1, mb: 1, alignItems: "center" }}
          >
            {showPriorityTypes.map((type) => (
              <Chip
                key={type}
                label={type}
                onClick={() =>
                  arrayHelpers.push(
                    getEmptyExpenseEntry(
                      type,
                      planStatus?.companyId,
                      planStatus?.user?.id,
                      rentalJourneyId,
                      "RENTAL_EXPENSE"
                    )
                  )
                }
                color="primary"
                variant="outlined"
                icon={<AddIcon sx={{ fontSize: "1rem" }} />}
                sx={{
                  width: "auto",
                  justifyContent: "flex-start",
                  height: 25,
                  borderRadius: 2,
                  "& .MuiChip-label": {
                    flex: 1,
                    textAlign: "left",
                    pl: 1,
                    fontSize: "0.75rem",
                  },
                }}
              />
            ))}

            {showRemainingTypes.length > 0 && (
              <>
                {!shouldShowAllTypes && !showAllExpenseTypes && (
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => setShowAllExpenseTypes(true)}
                    sx={{
                      fontSize: "0.7rem",
                      textTransform: "none",
                      ml: "auto",
                    }}
                  >
                    View More
                  </Button>
                )}

                {(shouldShowAllTypes || showAllExpenseTypes) && (
                  <>
                    {showRemainingTypes.map((type) => (
                      <Chip
                        key={type}
                        label={type}
                        onClick={() =>
                          arrayHelpers.push(
                            getEmptyExpenseEntry(
                              type,
                              planStatus?.companyId,
                              planStatus?.user?.id,
                              rentalJourneyId,
                              "RENTAL_EXPENSE"
                            )
                          )
                        }
                        color="primary"
                        variant="outlined"
                        icon={<AddIcon sx={{ fontSize: "1rem" }} />}
                        sx={{
                          width: "auto",
                          justifyContent: "flex-start",
                          height: 25,
                          borderRadius: 2,
                          "& .MuiChip-label": {
                            flex: 1,
                            textAlign: "left",
                            pl: 1,
                            fontSize: "0.75rem",
                          },
                        }}
                      />
                    ))}
                  </>
                )}
              </>
            )}
          </Stack>
        </Paper>
      );
    }

    // For non-outsourced, show all types as before
    return (
      <Paper
        elevation={3}
        sx={{
          p: 1.5,
          borderRadius: 2,
          position: "sticky",
          top: 16,
        }}
      >
        <Typography variant="subtitle1" sx={{ mb: 1, fontSize: "0.75rem" }}>
          Select expense type to add:
        </Typography>
        <Stack direction="row" flexWrap="wrap" sx={{ gap: 1 }}>
          {availableTypes.map((type) => (
            <Chip
              key={type}
              label={type}
              onClick={() =>
                arrayHelpers.push(
                  getEmptyExpenseEntry(
                    type,
                    planStatus?.companyId,
                    planStatus?.user?.id,
                    rentalJourneyId,
                    "RENTAL_EXPENSE"
                  )
                )
              }
              color="primary"
              variant="outlined"
              icon={<AddIcon sx={{ fontSize: "1rem" }} />}
              sx={{
                width: "auto",
                justifyContent: "flex-start",
                height: 25,
                borderRadius: 2,
                "& .MuiChip-label": {
                  flex: 1,
                  textAlign: "left",
                  pl: 1,
                  fontSize: "0.75rem",
                },
              }}
            />
          ))}
        </Stack>
      </Paper>
    );
  };

  const renderExpensesByType = (
    type: string,
    expenses: ExpenseEntry[],
    arrayHelpers: FieldArrayRenderProps,
    formikProps: FormikProps<ExpenseFormData>
  ) => {
    // Get actual indices of this type in the original expenses array using single loop
    const typeExpenseIndices = expenses.reduce<number[]>(
      (indices, expense, index) => {
        if (expense.type === type) {
          indices.push(index);
        }
        return indices;
      },
      []
    );

    if (typeExpenseIndices.length === 0) return null;

    return (
      <Box key={type} sx={{ mb: 4 }}>
        <Typography variant="h6" color="primary" sx={{ mb: 2 }}>
          {type} Expenses
        </Typography>

        {typeExpenseIndices.map((index, typeIndex) => {
          return (
            <ExpenseForm
              key={index}
              type={type}
              index={index}
              typeIndex={typeIndex}
              onDelete={() => {
                if (!expenses[index]?.isNew) {
                  formikProps.setFieldValue(
                    `expenses.${index}.isDeleted`,
                    true
                  );
                } else {
                  arrayHelpers.remove(index);
                }
                const keyToDelete = expenses[index]?.id;
                if (formikProps.values.odometerReadings?.has(keyToDelete!)) {
                  const newMap = new Map(formikProps.values.odometerReadings);
                  newMap.delete(keyToDelete!);
                  formikProps.setFieldValue("odometerReadings", newMap);
                }
              }}
              formikProps={formikProps}
            />
          );
        })}

        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Button
            startIcon={<AddIcon />}
            onClick={() =>
              arrayHelpers.push(
                getEmptyExpenseEntry(
                  type,
                  planStatus?.companyId,
                  planStatus?.user?.id,
                  rentalJourneyId,
                  "RENTAL_EXPENSE"
                )
              )
            }
            variant="outlined"
            size="small"
            sx={{
              fontSize: "0.75rem",
              height: "30px",
            }}
          >
            Add Another {type} Expense
          </Button>
        </Box>
      </Box>
    );
  };

  const validateStep = (values: ExpenseFormData) => {
    return values.expenses.length > 0 || selectedFiles?.length > 0;
  };

  if (isSubmitting || isLoading) {
    return (
      <Loading
        message={isSubmitting ? "Submitting..." : "Loading Expenses..."}
        fullScreen={true}
      />
    );
  }
  const handleSuccess = (
    vehicleId: string,
    vehicleNumber: string,
    vehicleType: string
  ) => {
    navigate(
      `/rental/${rentalJourneyId}/logs?type=${vehicleType}&vehicleId=${vehicleId}&vehicleNumber=${vehicleNumber}`
    );
  };

  const handleAddDetails = () => {
    setVehicleModalOpen(true);
  };

  if (isScheduled) {
    return (
      <>
        <AddVehicleAndOwner
          handleAddDetails={handleAddDetails}
          from="rental-expenses"
        />
        <AddScheduleVehicleModal
          open={vehicleModalOpen}
          onClose={() => setVehicleModalOpen(false)}
          rentalJourneyId={rentalJourneyId!}
          onSuccess={(vehicleId, vehicleNumber, vehicleType) =>
            handleSuccess(vehicleId, vehicleNumber, vehicleType)
          }
        />
      </>
    );
  }

  return (
    <>
      <Formik
        key={formKey}
        initialValues={initialValues}
        enableReinitialize={true}
        validationSchema={validationSchema}
        validateOnChange={true}
        // validateOnBlur={true}
        onSubmit={(values) =>
          handleExpenseSubmit(
            values,
            initialValues,
            rentalJourneyId!,
            rentalJourneyId!,
            setIsSubmitting,
            showToast,
            () => {
              setIsEditMode(false);
              setIsAddMode(false);
              setSelectedFiles([]);
              setUploadedFiles([]);
              fetchExpenseDetails();
            },
            {
              vehicleId: vehicleId || "",
              vehicleNumber: vehicleNumber || "",
            },
            selectedFiles
          )
        }
      >
        {(formikProps: FormikProps<ExpenseFormData>) => {
          const hasVehicleSelected = Boolean(formikProps.values.vehicleNumber);

          return (
            <Box
              component="form"
              noValidate
              onSubmit={(e: FormEvent<HTMLFormElement>) => {
                e.preventDefault();
                formikProps.handleSubmit(e);
              }}
            >
              {authLoading ? (
                <Loading message="Loading..." fullScreen={true} />
              ) : (
                <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                  <Grid item xs={12} md={12}>
                    <Box
                      sx={{
                        bgcolor: "#fff",
                        borderRadius: 1,
                        p: 2,
                        mt: 2,
                        width: "100%",
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 2,
                      }}
                    >
                      <Box
                        sx={{
                          width: "100%",
                          display: "flex",
                          flexDirection: "row",
                          justifyContent: "space-between",
                          alignItems: "center",
                          // gap: 2
                        }}
                      >
                        <Typography variant="subtitle2">
                          Total Expense Amount
                        </Typography>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontStyle: "italic" }}
                        >
                          {formatCurrency(
                            formikProps.values.expenses.reduce(
                              (acc, expense) => acc + Number(expense.amount),
                              0
                            )
                          )}
                        </Typography>
                      </Box>
                      {!isAddMode && !isEditMode && (
                        <Button
                          variant="contained"
                          startIcon={<AddIcon sx={{ width: 12, height: 12 }} />}
                          onClick={() => {
                            setIsAddMode(true);
                            setInitialValues({
                              vehicleNumber: vehicleNumber || "",
                              vehicleId: vehicleId || "",
                              expenses: [],
                            });
                            fetchOdometerReadings();
                            setFormKey((prev) => prev + 1);
                          }}
                          sx={{ minWidth: 100, height: 30, fontSize: "0.7rem" }}
                        >
                          Add
                        </Button>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              )}
              {isEditMode || isAddMode ? (
                <>
                  <FieldArray name="expenses">
                    {(arrayHelpers: FieldArrayRenderProps) => (
                      <Grid container spacing={3}>
                        {!isEditMode && (
                          <Grid
                            item
                            xs={12}
                            md={12}
                            sx={{
                              position: "sticky",
                              top: 5,
                              zIndex: 100,
                            }}
                          >
                            {renderExpenseTypeSelector(
                              arrayHelpers,
                              formikProps.values.expenses,
                              hasVehicleSelected,
                              formikProps.values.vehicle?.acquisitionType || ""
                            )}
                          </Grid>
                        )}
                        <Grid item xs={12} md={12}>
                          {hasVehicleSelected ? (
                            <>
                              {(formikProps.values.vehicle?.acquisitionType ===
                              "Outsourced"
                                ? OUTSOURCED_EXPENSE_TYPES
                                : EXPENSE_TYPES
                              ).map((type) => {
                                return (
                                  type !== "Other" &&
                                  renderExpensesByType(
                                    type,
                                    formikProps.values.expenses,
                                    arrayHelpers,
                                    formikProps
                                  )
                                );
                              })}
                              {hasOtherExpense(formikProps.values.expenses) && (
                                <GenericOtherExpenseFields
                                  index={formikProps.values.expenses.length}
                                  masterCategory="RENTAL_EXPENSE"
                                  masterReferenceId={rentalJourneyId}
                                  companyId={planStatus?.companyId!}
                                  createdBy={planStatus?.user?.id}
                                  defaultDate={
                                    new Date().toISOString().split("T")[0]
                                  }
                                />
                              )}
                            </>
                          ) : (
                            <Paper
                              elevation={0}
                              sx={{
                                p: 4,
                                borderRadius: 2,
                                bgcolor: "action.disabledBackground",
                                textAlign: "center",
                              }}
                            >
                              <Typography
                                variant="h6"
                                color="text.secondary"
                                gutterBottom
                              >
                                No Vehicle Number Provided
                              </Typography>
                              <Typography
                                variant="body1"
                                color="text.secondary"
                              >
                                Please provide a valid vehicle number to start
                                adding expenses.
                              </Typography>
                            </Paper>
                          )}
                        </Grid>
                      </Grid>
                    )}
                  </FieldArray>
                  <Divider sx={{ my: 2, width: "100%" }} />
                  <Box
                    sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}
                  >
                    <Button
                      variant="outlined"
                      startIcon={<CancelIcon sx={{ width: 12, height: 12 }} />}
                      onClick={() => {
                        setIsEditMode(false);
                        setIsAddMode(false);
                        setFormKey((prev) => prev + 1);
                        setSelectedFiles([]);
                        fetchExpenseDetails();
                      }}
                      sx={{
                        minWidth: 100,
                        height: 30,
                        fontSize: "0.7rem",
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => {
                        displayErrorToast(formikProps.errors, showToast);
                        formikProps.handleSubmit();
                      }}
                      startIcon={<SaveIcon sx={{ width: 12, height: 12 }} />}
                      disabled={
                        !validateStep(formikProps.values) || isSubmitting
                      }
                      sx={{
                        minWidth: 100,
                        height: 30,
                        fontSize: "0.7rem",
                      }}
                    >
                      {isSubmitting ? "Saving..." : "Save"}
                    </Button>
                  </Box>
                </>
              ) : (
                <>
                  <ExpensesList
                    expenses={formikProps.values.expenses}
                    vehicleNumber={vehicleNumber || ""}
                    vehicleId={vehicleId || ""}
                    setIsEditMode={setIsEditMode}
                    setInitialValues={setInitialValues}
                    setFormKey={setFormKey}
                    acquisitionType={
                      formikProps.values.vehicle?.acquisitionType || ""
                    }
                    vehicle={formikProps.values.vehicle || null}
                  />
                  {changedExpenses?.length > 0 && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Grid item xs={12}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: "bold",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            color: "gray",
                            fontSize: "0.75rem",
                          }}
                          onClick={() => {
                            setShowChangeLogs(!showChangeLogs);
                          }}
                        >
                          Expense Change Logs
                          {showChangeLogs ? (
                            <KeyboardArrowUp />
                          ) : (
                            <KeyboardArrowDown />
                          )}
                        </Typography>
                        {showChangeLogs && (
                          <>
                            <DeletedExpenseDetails
                              deletedExpenseDetails={changedExpenses}
                            />
                          </>
                        )}
                      </Grid>
                    </>
                  )}
                </>
              )}
            </Box>
          );
        }}
      </Formik>
    </>
  );
}

export default RentalExpenses;

const ExpensesList = ({
  expenses,
  vehicleNumber,
  vehicleId,
  setIsEditMode,
  setInitialValues,
  setFormKey,
  acquisitionType,
  vehicle,
}: {
  expenses: ExpenseEntry[];
  vehicleNumber: string;
  vehicleId: string;
  setIsEditMode: (isEditMode: boolean) => void;
  setInitialValues: (initialValues: ExpenseFormData) => void;
  setFormKey: (value: React.SetStateAction<number>) => void;
  acquisitionType: string;
  vehicle: Vehicle | null;
}) => {
  const tableList = [
    "Expense Type",
    "Amount",
    "Paid To",
    "Location",
    "Contact Number",
    "Notes",
    "Actions",
  ];
  return (
    <Paper>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {tableList.map((item) => (
                <TableCell key={item} sx={tableHeaderStyles}>
                  {item}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {expenses?.length > 0 ? (
              expenses.map((expense) => (
                <TableRow>
                  <TableCell sx={tableCellStyles}>{expense.type}</TableCell>
                  <TableCell sx={tableCellStyles}>
                    {formatCurrency(expense.amount)}
                  </TableCell>
                  <TableCell sx={tableCellStyles}>
                    {expense.paidTo || "-"}
                  </TableCell>
                  <TableCell sx={tableCellStyles}>
                    {expense.location || "-"}
                  </TableCell>
                  <TableCell sx={tableCellStyles}>
                    {expense.contactNumber || "-"}
                  </TableCell>
                  <TableCell sx={tableCellStyles}>
                    {expense.notes || "-"}
                  </TableCell>
                  <TableCell sx={tableCellStyles}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setIsEditMode(true);
                        setInitialValues({
                          vehicleNumber: vehicleNumber || "",
                          vehicleId: vehicleId || "",
                          expenses: [JSON.parse(JSON.stringify(expense))],
                          acquisitionType: acquisitionType,
                          vehicle: vehicle,
                        });
                        setFormKey((prev) => prev + 1);
                      }}
                      title="Edit Expense"
                      sx={{
                        py: 0.25,
                        px: 0.5,
                        minWidth: "auto",
                        fontSize: "0.75rem",
                        mr: 1,
                      }}
                    >
                      Edit
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={tableList.length}
                  sx={tableCellStyles}
                  align="center"
                >
                  No expenses found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};
